import Vue from 'vue'
import axios from '@axios'
import moment from 'moment'
import useJwt from '@/auth/jwt/useJwt'
import { statusTypes } from '@/mixins'
import _ from 'lodash'
import { formatTime } from '@/utils/dateUtils'
import { toMiliseconds } from '@/utils/promise-util'
import alarmAudio from '@/assets/audio/alarm.mp3'
import notificationAudio from '@/assets/audio/notification.mp3'

const notificationMessage = new Audio(notificationAudio)
const alarmSound = new Audio(alarmAudio)

const deliveryStatusEnum = statusTypes.computed.deliveryStatusEnum()
const MAX_WEBSOCKET_RECONNECT_DELAY = 5000
const INITIAL_WEBSOCKET_RECONNECT_DELAY = 1000

const cloneDashboardData = value => JSON.parse(JSON.stringify(value))
const getEntityId = entity => entity?.motoboyId ?? entity?.id
const hasSameId = (left, right) =>
  left !== null && left !== undefined && right !== null && right !== undefined && String(left) === String(right)
const getResponseResults = data => data?.results ?? data?.motoboys ?? data ?? []
const getReconnectDelay = attempts =>
  Math.min(INITIAL_WEBSOCKET_RECONNECT_DELAY * 2 ** attempts, MAX_WEBSOCKET_RECONNECT_DELAY)

const getDashboardSnapshot = state => ({
  allOrders: cloneDashboardData(state.allOrders),
  motoboys: cloneDashboardData(state.motoboys),
})

function playSoundNTimes({ sound, times }) {
  let count = 0

  // Function to play sound and increment count
  function playSound() {
    if (count < times) {
      // setTimeout(() => {
      sound
        .play()
        .then(() => {
          count += 1
          // Wait for the sound to finish before playing it again
          // eslint-disable-next-line no-param-reassign
          sound.onended = playSound
        })
        .catch(error => {
          console.error('Error playing sound:', error)
        })
      // }, interval || 1000)
    }
  }

  // Start playing sound
  playSound()
}

const getInitialState = () => ({
  filter: {
    storeId: null,
  },
  webSocket: {
    delivery: {
      config: null,
      ready: false,
      connected: false,
      ws: null,
      currentChannelId: null,
      reconnectTimer: null,
      reconnectAttempts: 0,
      shouldReconnect: true,
      synchronizing: false,
      pendingEvents: [],
    },
    chat: {
      config: null,
      ready: false,
      connected: false,
      ws: null,
      currentChannelId: null,
      reconnectTimer: null,
      reconnectAttempts: 0,
      shouldReconnect: true,
    },
  },
  allOrders: [],
  motoboys: [],
  motoboyOperationPending: {},
  assignmentOperationPending: {},
  chats: [],
  contacts: [],
  activeChat: null,
  lastFetch: null,
  orderItems: {},
  lastTimeHasUnansweredChats: null,
  activeTab: 'today',
  deliverySuppliers: [],
  deliverySuppliersByStore: {},
  unansweredMessageAlarmTimer: null,
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {
    ready(state) {
      return state.webSocket.delivery.ready && state.webSocket.chat.ready
    },
    chatWebSocketConnected(state) {
      return state.webSocket.chat.connected
    },
    deliveryWebSocketConnected(state) {
      return state.webSocket.delivery.connected
    },
    // Funcionalidade de Motoboys da loja selecionada (Store.motoboyEnabled). Lojas antigas,
    // ou payloads sem o campo, seguem com a funcionalidade habilitada.
    motoboyEnabled(state, getters, rootState) {
      const store = (rootState.app?.stores || []).find(item =>
        hasSameId(item.id, state.filter.storeId)
      )
      return store?.motoboyEnabled ?? true
    },
    motoboys(state) {
      const isUnderResponsibility = order =>
        order.deliveryStatus !== deliveryStatusEnum.COMPLETED &&
        order.deliveryStatus !== deliveryStatusEnum.CANCELED
      return state.motoboys.map(motoboy => {
        const motoboyId = getEntityId(motoboy)
        return {
          ...motoboy,
          // Only the deliveries the motoboy is currently responsible for
          // (assigned and not yet completed/canceled), not their full history.
          deliveryOrders: state.allOrders.filter(
            order =>
              hasSameId(order.motoboyId ?? order.motoboy?.id, motoboyId) &&
              isUnderResponsibility(order)
          ),
        }
      })
    },
    presentMotoboys(state) {
      return state.motoboys.filter(motoboy => {
        const presenceState = motoboy.presenceState ?? motoboy.state ?? motoboy.presence?.state
        return String(presenceState).toLowerCase() === 'present'
      })
    },
    // Quem pode receber pedido: presentes e os que estão em entrega — nesses, o pedido entra
    // na entrega em andamento (DeliveryMotoboyService.AssignMotoboy).
    assignableMotoboys(state, getters) {
      return getters.motoboys
        .map(motoboy => {
          const presenceState = String(
            motoboy.presenceState ?? motoboy.state ?? motoboy.presence?.state
          ).toLowerCase()
          const inDelivery =
            presenceState === 'unavailable' &&
            motoboy.deliveryOrders.some(
              order => order.deliveryStatus === deliveryStatusEnum.IN_PROGRESS
            )
          return { ...motoboy, inDelivery, assignable: presenceState === 'present' || inDelivery }
        })
        .filter(motoboy => motoboy.assignable)
    },
    // Pedidos de entrega ainda sem motoboy e que podem receber um: não saíram para entrega e
    // nenhum fornecedor comprometeu entregador (DeliverySupplierHandover.HasCommittedDriver).
    ordersWaitingMotoboy(state) {
      const { COMPLETED, CANCELED, IN_PROGRESS } = deliveryStatusEnum
      return state.allOrders.filter(order => {
        if (order.motoboyId ?? order.motoboy?.id) return false
        if ([COMPLETED, CANCELED, IN_PROGRESS].includes(order.deliveryStatus)) return false
        const assignmentStatus = String(order.motoboyAssignmentStatus || '').toLowerCase()
        if (assignmentStatus && assignmentStatus !== 'waitingassignment') return false
        if (!order.deliverySupplierId) return true
        const supplierStatus = String(
          order.deliverySupplierStatus || order.deliveryOrder?.status || ''
        ).toLowerCase()
        return !['waiting', 'driverarrived', 'delivering', 'completed'].includes(supplierStatus)
      })
    },
    isMotoboyOperationPending: state => motoboyId =>
      !!state.motoboyOperationPending[String(motoboyId)],
    isAssignmentOperationPending: state => orderId =>
      !!state.assignmentOperationPending[String(orderId)],
    groupedChats(state) {
      return _.groupBy(
        state.activeChat?.messages,
        message => `${message.userId}.${formatTime(message.created)}`
      )
    },
    orders(state) {
      return state.allOrders.filter(r => r.deliveryStatus === deliveryStatusEnum.PENDING)
    },
    preparingOrders(state) {
      return state.allOrders.filter(r => r.deliveryStatus === deliveryStatusEnum.PREPARING)
    },
    deliveringOrders(state) {
      return state.allOrders.filter(r => r.deliveryStatus === deliveryStatusEnum.IN_PROGRESS)
    },
    deliveredOrders(state) {
      return state.allOrders.filter(r => r.deliveryStatus === deliveryStatusEnum.COMPLETED)
    },
    hasUnansweredChats(state) {
      return !!state.allOrders.find(order => order.hasChatPendingAnswer)
    },
    // eslint-disable-next-line no-unused-vars
    startOfToday(state) {
      // moment() uses the operator's local timezone
      return moment().startOf('day')
    },
    // eslint-disable-next-line no-unused-vars
    endOfToday(state) {
      return moment().endOf('day')
    },
    delayedOrders(state, getters) {
      const { startOfToday } = getters
      return state.allOrders
        .filter(
          order =>
            !!order.deliveryDate &&
            moment(order.deliveryDate).isBefore(startOfToday)
        )
        .slice()
        .sort((a, b) => {
          const diff = moment(a.deliveryDate).valueOf() - moment(b.deliveryDate).valueOf()
          if (diff !== 0) return diff
          return a.id - b.id
        })
    },
    todayOrders(state, getters) {
      const { startOfToday, endOfToday } = getters
      return state.allOrders
        .filter(
          order =>
            !!order.deliveryDate &&
            moment(order.deliveryDate).isSameOrAfter(startOfToday) &&
            moment(order.deliveryDate).isSameOrBefore(endOfToday)
        )
        .slice()
        .sort((a, b) => {
          const diff = moment(a.deliveryDate).valueOf() - moment(b.deliveryDate).valueOf()
          if (diff !== 0) return diff
          return a.id - b.id
        })
    },
    upcomingOrders(state, getters) {
      const { endOfToday } = getters
      return state.allOrders
        .filter(
          order =>
            !!order.deliveryDate &&
            moment(order.deliveryDate).isAfter(endOfToday)
        )
        .slice()
        .sort((a, b) => {
          const diff = moment(a.deliveryDate).valueOf() - moment(b.deliveryDate).valueOf()
          if (diff !== 0) return diff
          return a.id - b.id
        })
    },
    tabBadge(state, getters) {
      return tab => {
        let count = 0
        if (tab === 'today') count = getters.todayOrders.length
        else if (tab === 'delayed') count = getters.delayedOrders.length
        else if (tab === 'upcoming') count = getters.upcomingOrders.length
        return count > 9999 ? '9999+' : count
      }
    },
    statusCountsByTab(state, getters) {
      const { startOfToday, endOfToday } = getters
      const emptyCounts = () => ({
        [deliveryStatusEnum.PENDING]: 0,
        [deliveryStatusEnum.PREPARING]: 0,
        [deliveryStatusEnum.IN_PROGRESS]: 0,
        [deliveryStatusEnum.COMPLETED]: 0,
        chatPending: 0,
      })
      const result = {
        delayed: emptyCounts(),
        today: emptyCounts(),
        upcoming: emptyCounts(),
      }
      state.allOrders.forEach(order => {
        if (!order.deliveryDate) return
        const deliveryMoment = moment(order.deliveryDate)
        let bucket
        if (deliveryMoment.isBefore(startOfToday)) bucket = 'delayed'
        else if (deliveryMoment.isSameOrBefore(endOfToday)) bucket = 'today'
        else bucket = 'upcoming'
        if (Object.prototype.hasOwnProperty.call(result[bucket], order.deliveryStatus)) {
          result[bucket][order.deliveryStatus] += 1
        }
        if (order.hasChatPendingAnswer) {
          result[bucket].chatPending += 1
        }
      })
      return result
    },
    ordersByTabAndStatus(state, getters) {
      const sortFn = (a, b) => {
        const diff = moment(a.deliveryDate).valueOf() - moment(b.deliveryDate).valueOf()
        if (diff !== 0) return diff
        return a.id - b.id
      }
      const groupByStatus = orders => ({
        [deliveryStatusEnum.PENDING]: orders
          .filter(o => o.deliveryStatus === deliveryStatusEnum.PENDING)
          .slice()
          .sort(sortFn),
        [deliveryStatusEnum.PREPARING]: orders
          .filter(o => o.deliveryStatus === deliveryStatusEnum.PREPARING)
          .slice()
          .sort(sortFn),
        [deliveryStatusEnum.IN_PROGRESS]: orders
          .filter(o => o.deliveryStatus === deliveryStatusEnum.IN_PROGRESS)
          .slice()
          .sort(sortFn),
        [deliveryStatusEnum.COMPLETED]: orders
          .filter(o => o.deliveryStatus === deliveryStatusEnum.COMPLETED)
          .slice()
          .sort(sortFn),
      })
      return {
        delayed: groupByStatus(getters.delayedOrders),
        today: groupByStatus(getters.todayOrders),
        upcoming: groupByStatus(getters.upcomingOrders),
      }
    },
  },

  mutations: {
    SET_CHAT_READY(state, ready) {
      state.webSocket.chat.ready = ready
    },
    SET_DELIVERY_READY(state, ready) {
      state.webSocket.delivery.ready = ready
    },
    SET_DELIVERY_ORDER(state, { orderId, deliveryOrderId, deliverySupplier }) {
      const order = state.allOrders.find(o => o.id === orderId)
      if (order) {
        Vue.set(order, 'deliveryOrderId', deliveryOrderId)
        if (deliveryOrderId === null) {
          if (order.deliveryOrder) {
            Vue.set(order.deliveryOrder, 'status', 'canceled')
          }
          Vue.set(order, 'deliverySupplierStatus', 'canceled')
          Vue.set(order, 'deliveryStatus', deliveryStatusEnum.PENDING)
        } else {
          if (order.deliveryOrder) {
            Vue.set(order.deliveryOrder, 'status', 'finding')
          } else {
            Vue.set(order, 'deliveryOrder', { orderId: deliveryOrderId, status: 'finding' })
          }
          Vue.set(order, 'deliverySupplierStatus', 'finding')
          if (deliverySupplier) {
            Vue.set(order, 'deliverySupplier', deliverySupplier)
          }
        }
      }
    },
    SET_CHAT_WEBSOCKET_CONNECTED(state, connected) {
      state.webSocket.chat.connected = connected
    },
    SET_DELIVERY_WEBSOCKET_CONNECTED(state, connected) {
      state.webSocket.delivery.connected = connected
    },
    SET_WEBSOCKET_INSTANCE(state, { type, ws }) {
      state.webSocket[type].ws = ws
    },
    SET_WEBSOCKET_CHANNEL(state, { type, channelId }) {
      state.webSocket[type].currentChannelId = channelId
    },
    SET_WEBSOCKET_RECONNECT_TIMER(state, { type, timer }) {
      state.webSocket[type].reconnectTimer = timer
    },
    SET_WEBSOCKET_RECONNECT_ATTEMPTS(state, { type, attempts }) {
      state.webSocket[type].reconnectAttempts = attempts
    },
    SET_WEBSOCKET_SHOULD_RECONNECT(state, { type, shouldReconnect }) {
      state.webSocket[type].shouldReconnect = shouldReconnect
    },
    SET_DELIVERY_SYNCHRONIZING(state, synchronizing) {
      state.webSocket.delivery.synchronizing = synchronizing
    },
    QUEUE_DELIVERY_EVENT(state, event) {
      state.webSocket.delivery.pendingEvents.push(event)
    },
    CLEAR_PENDING_DELIVERY_EVENTS(state) {
      state.webSocket.delivery.pendingEvents = []
    },
    SET_MOTOBOYS(state, motoboys) {
      state.motoboys = Array.isArray(motoboys) ? motoboys : []
    },
    MERGE_MOTOBOY(state, motoboyPayload) {
      if (!motoboyPayload) return
      const motoboyId = getEntityId(motoboyPayload)
      // Presence arrives as `state` on some payloads and `presenceState` on
      // others. Normalize both so the card's presence reading is never shadowed
      // by a stale value from a previous update.
      const presence = motoboyPayload.presenceState ?? motoboyPayload.state
      const normalized =
        presence === undefined || presence === null
          ? motoboyPayload
          : { ...motoboyPayload, presenceState: presence, state: presence }
      const index = state.motoboys.findIndex(motoboy =>
        hasSameId(getEntityId(motoboy), motoboyId)
      )
      if (index === -1) {
        state.motoboys.push(normalized)
        return
      }
      Vue.set(state.motoboys, index, {
        ...state.motoboys[index],
        ...normalized,
      })
    },
    SET_MOTOBOY_PRESENCE(state, { motoboyId, presenceState }) {
      const motoboy = state.motoboys.find(item =>
        hasSameId(getEntityId(item), motoboyId)
      )
      if (!motoboy) return
      Vue.set(motoboy, 'presenceState', presenceState)
      Vue.set(motoboy, 'state', presenceState)
    },
    SET_MOTOBOY_OPERATION_PENDING(state, { motoboyId, pending }) {
      Vue.set(state.motoboyOperationPending, String(motoboyId), pending)
    },
    SET_ASSIGNMENT_OPERATION_PENDING(state, { orderId, pending }) {
      Vue.set(state.assignmentOperationPending, String(orderId), pending)
    },
    RESTORE_DASHBOARD_SNAPSHOT(state, snapshot) {
      state.allOrders = snapshot.allOrders
      state.motoboys = snapshot.motoboys
    },
    UPSERT_ORDER(state, orderPayload) {
      if (!orderPayload?.id) return
      const index = state.allOrders.findIndex(order => order.id === orderPayload.id)
      if (index === -1) {
        state.allOrders.push(orderPayload)
      } else {
        Vue.set(state.allOrders, index, {
          ...state.allOrders[index],
          ...orderPayload,
        })
      }
    },
    MERGE_ORDER_DELIVERY_STATE(state, { orderId, changes, dispatch: dispatchPayload }) {
      const order = state.allOrders.find(item => item.id === orderId)
      if (!order) return
      Object.entries(changes || {}).forEach(([key, value]) => Vue.set(order, key, value))
      if (dispatchPayload) {
        const dispatches = [...(order.deliveryDispatches || order.dispatches || [])]
        const dispatchIndex = dispatches.findIndex(
          item =>
            (dispatchPayload.id && item.id === dispatchPayload.id) ||
            (dispatchPayload.deliverySupplierId &&
              item.deliverySupplierId === dispatchPayload.deliverySupplierId)
        )
        if (dispatchIndex === -1) dispatches.push(dispatchPayload)
        else dispatches.splice(dispatchIndex, 1, { ...dispatches[dispatchIndex], ...dispatchPayload })
        Vue.set(order, 'deliveryDispatches', dispatches)
      }
    },
    SET_UNANSWERED_MESSAGE_ALARM_TIMER(state, timer) {
      state.unansweredMessageAlarmTimer = timer
    },
    SET_FILTER(state, filter) {
      state.filter = filter
    },
    SET_ALL_ORDERS(state, orders) {
      state.allOrders = orders
    },
    SET_LAST_FETCHED(state) {
      state.lastFetch = moment()
    },

    SET_ORDER_ITEMS(state, { orderId, items }) {
      Vue.set(state.orderItems, orderId, items)
    },

    SET_CONTACTS(state, orders) {
      state.contacts = orders.map(order => {
        const [firstName] = order.customer.name.split(' ')
        return {
          id: order.id,
          fullName: `${firstName} - Ped. #${order.id}`,
          status: 'online',
          channelId: `order-${state.filter.storeId}-${order.customer.id}-${order.id}`,
          chat: {
            lastMessage: {
              time: Date.now,
            },
          },
        }
      })
    },

    CLEAN_STATE(state) {
      const defaulData = getInitialState()
      Object.keys(defaulData).forEach(key => {
        state[key] = defaulData[key]
      })
    },

    SET_OR_ADD_ORDER(state, orderPayload) {
      if (!orderPayload) return
      const existingOrderIndex = state.allOrders.findIndex(order => order.id === orderPayload.id)

      if (existingOrderIndex !== -1) {
        const existingOrder = state.allOrders[existingOrderIndex]

        if (state.orderItems[orderPayload.id] && orderPayload.itemsModified) {
          delete state.orderItems[orderPayload.id]
        }

        // Use moment to compare eventDate with modifiedDate to ensure only newer events are processed
        const isNewerEvent = moment(orderPayload.eventDate).isSameOrAfter(
          moment(existingOrder.modifiedDate)
        )

        if (isNewerEvent) {
          if (orderPayload.deliveryStatus === deliveryStatusEnum.CANCELED) {
            state.allOrders.splice(existingOrderIndex, 1)
          } else {
            const merged = {
              ...existingOrder,
              ...orderPayload,
            }
            // The websocket sends a minimal payload when the sale is finalized
            // (Completed/Canceled). It only contains { id, isPaid, deliveryStatus,
            // deliveryDate }, omitting deliveryOrder and deliverySupplierStatus.
            // Force the supplier status to mirror the sale status so the card
            // badge reflects "Entrega concluída" / "Entrega cancelada" without
            // depending on a separate event.
            if (orderPayload.deliveryStatus === deliveryStatusEnum.COMPLETED) {
              merged.deliverySupplierStatus = 'completed'
              if (existingOrder.deliveryOrder) {
                merged.deliveryOrder = {
                  ...existingOrder.deliveryOrder,
                  status: 'completed',
                }
              }
            }
            Vue.set(state.allOrders, existingOrderIndex, merged)
          }
        }
      } else if (orderPayload.deliveryStatus !== deliveryStatusEnum.CANCELED) {
        state.allOrders.push(orderPayload)
      }
    },

    SET_MESSAGE_STATUS(state, message) {
      const existingMessage = state.activeChat?.messages.find(
        messageItem => messageItem.messageId === message.messageId
      )
      if (existingMessage) {
        Vue.set(existingMessage, message.action, Date.now())
      }
    },

    ADD_CHAT_MESSAGE(state, message) {
      const currentChat = state.chats.find(chat => chat.channelId === message.channelId)
      console.log('ADD_CHAT_MESSAGE', message, currentChat)
      if (currentChat) {
        currentChat.lastMessage = message
        if (message.userId.indexOf('C-') === 0) {
          currentChat.hasChatPendingAnswer = true
          if (currentChat.channelId !== state.activeChat?.channelId) {
            currentChat.totalUnreadMessages = (currentChat.totalUnreadMessages || 0) + 1
          }
        } else {
          currentChat.hasChatPendingAnswer = false
        }
        currentChat.totalMessages = (currentChat.totalMessages || 0) + 1
      }

      if (state.activeChat?.channelId === message.channelId) {
        state.activeChat.messages.push(message)
      }

      const [orderId] = message.channelId.split('-').slice(-1)
      // eslint-disable-next-line eqeqeq
      const existingOrder = state.allOrders.find(order => order.id == orderId)
      if (existingOrder) {
        if (message.userId.indexOf('C-') === 0) {
          Vue.set(existingOrder, 'hasChatPendingAnswer', true)
          if (message.channelId !== state.activeChat?.channelId) {
            Vue.set(
              existingOrder,
              'totalUnreadMessages',
              (existingOrder.totalUnreadMessages || 0) + 1
            )
          }
          Vue.set(existingOrder, 'totalMessages', (existingOrder.totalMessages || 0) + 1)
        }
        Vue.set(existingOrder, 'hasChatPendingAnswer', message.userId.indexOf('C-') === 0)
      }
    },

    SET_ACTIVE_CHAT(state, chat) {
      state.activeChat = chat
    },

    SET_ACTIVE_CHATS(state, chats) {
      chats.forEach(chat => {
        const [orderId] = chat.channelId.split('-').slice(-1)
        // eslint-disable-next-line eqeqeq
        const existingOrder = state.allOrders.find(order => order.id == orderId)
        if (existingOrder) {
          Vue.set(existingOrder, 'totalUnreadMessages', chat.totalUnreadMessages)
          Vue.set(existingOrder, 'totalMessages', chat.totalMessages)
          Vue.set(existingOrder, 'chatActive', true)
          Vue.set(
            existingOrder,
            'hasChatPendingAnswer',
            chat.lastMessage?.userId?.indexOf('C-') === 0
          )
        }
      })
      state.chats = chats
    },

    SET_READ_MESSAGES(state, messages) {
      if (messages.length) {
        const [channelId] = messages.map(message => message.channelId)
        const existingChat = state.chats.find(chat => chat.channelId === channelId)
        const now = Date.now()
        messages.forEach(message => {
          let chatMessage = existingChat.messages?.find(
            chatMessageItem => chatMessageItem.messageId === message.messageId
          )
          if (chatMessage) {
            chatMessage.read = now
          }

          chatMessage = state.activeChat.messages?.find(
            chatMessageItem => chatMessageItem.messageId === message.messageId
          )
          if (chatMessage) {
            chatMessage.read = now
          }
        })
        if (existingChat) {
          existingChat.totalUnreadMessages = 0
        }
        const [orderId] = channelId.split('-').slice(-1)
        // eslint-disable-next-line eqeqeq
        const existingOrder = state.allOrders.find(order => order.id == orderId)
        if (existingOrder) {
          Vue.set(existingOrder, 'totalUnreadMessages', 0)
          Vue.set(existingOrder, 'chatActive', true)
        }
      }
    },

    ADD_CHAT(state, chat) {
      if (!state.chats.find(chatItem => chatItem.channelId === chat.channelId)) {
        state.chats.push(chat)
      }
    },

    SET_DELIVERY_WS_CONFIG(state, data) {
      state.webSocket.delivery.config = data
    },

    CLEAR_ACTIVE_CHAT(state) {
      state.activeChat = null
    },

    SET_ACTIVE_TAB(state, tab) {
      state.activeTab = tab
    },

    SET_DELIVERY_SUPPLIERS(state, deliverySuppliers) {
      state.deliverySuppliers = deliverySuppliers
    },

    SET_DELIVERY_SUPPLIERS_FOR_STORE(state, { storeId, suppliers }) {
      const key = storeId == null ? 'all' : String(storeId)
      Vue.set(state.deliverySuppliersByStore, key, suppliers)
    },
  },

  actions: {
    async fetchDeliveryWebSocketConfig({ commit }) {
      const { data } = await axios.get('/api/settings/delivery/ws')
      commit('SET_DELIVERY_WS_CONFIG', data)
    },

    async fetchDeliveryOrders({ commit, state }) {
      const { storeId } = state.filter
      if (!storeId) {
        commit('SET_ALL_ORDERS', [])
        return []
      }
      const { data } = await axios.get(`/api/sales/delivery/store/${storeId}`, {
        params: {
          pageSize: 0,
          pageIndex: 9999,
          sortBy: 'DeliveryDate ASC',
        },
      })

      const orders = data.results || []
      commit('SET_LAST_FETCHED')
      commit('SET_CONTACTS', orders)
      commit('SET_ALL_ORDERS', orders)
      commit('SET_ACTIVE_CHATS', state.chats)
      return orders
    },

    async fetchMotoboys({ commit, state }) {
      const { storeId } = state.filter
      if (!storeId) {
        commit('SET_MOTOBOYS', [])
        return []
      }
      const { data } = await axios.get('/api/sales/delivery/motoboys', {
        params: { storeId },
      })
      const motoboys = getResponseResults(data)
      commit('SET_MOTOBOYS', motoboys)
      return motoboys
    },

    async resynchronizeDeliveryDashboard({ dispatch, state }) {
      await Promise.all([
        dispatch('fetchDeliveryOrders'),
        dispatch('fetchMotoboys'),
        // Needed so cards can resolve a dispatch's supplier name from its id when the
        // dispatch arrives/updates over the websocket (which only carries the id).
        dispatch('fetchDeliverySuppliers', { storeId: state.filter.storeId }),
      ])
    },

    async setMotoboyPresence({ commit, state }, { motoboyId, presenceState }) {
      const snapshot = getDashboardSnapshot(state)
      const normalizedState = String(presenceState).toLowerCase()
      const action = normalizedState === 'present' ? 'present' : 'absent'
      commit('SET_MOTOBOY_OPERATION_PENDING', { motoboyId, pending: true })
      commit('SET_MOTOBOY_PRESENCE', {
        motoboyId,
        presenceState: action === 'present' ? 'Present' : 'Absent',
      })
      try {
        const { data } = await axios.post(
          `/api/sales/delivery/motoboys/${motoboyId}/${action}?storeId=${state.filter.storeId}`,
        )
        if (Array.isArray(data)) commit('SET_MOTOBOYS', data)
        else if (data?.motoboys) commit('SET_MOTOBOYS', data.motoboys)
        else commit('MERGE_MOTOBOY', data?.motoboy || data)
        return data
      } catch (error) {
        commit('RESTORE_DASHBOARD_SNAPSHOT', snapshot)
        throw error
      } finally {
        commit('SET_MOTOBOY_OPERATION_PENDING', { motoboyId, pending: false })
      }
    },

    async startMotoboyDelivery({ commit, state, dispatch }, motoboyId) {
      const snapshot = getDashboardSnapshot(state)
      commit('SET_MOTOBOY_OPERATION_PENDING', { motoboyId, pending: true })
      let data
      try {
        const response = await axios.post(
          `/api/sales/delivery/motoboys/${motoboyId}/start-delivery?storeId=${state.filter.storeId}`
        )
        data = response.data
      } catch (error) {
        commit('RESTORE_DASHBOARD_SNAPSHOT', snapshot)
        commit('SET_MOTOBOY_OPERATION_PENDING', { motoboyId, pending: false })
        throw error
      }

      try {
        if (Array.isArray(data)) commit('SET_MOTOBOYS', data)
        else if (data?.motoboys) commit('SET_MOTOBOYS', data.motoboys)
        else commit('MERGE_MOTOBOY', data?.motoboy || data)
        const updatedOrders = data?.orders || []
        updatedOrders.forEach(order => commit('UPSERT_ORDER', order))

        const synchronization = []
        if (!Array.isArray(data) && !data?.motoboys) {
          synchronization.push(dispatch('fetchMotoboys'))
        }
        if (!updatedOrders.length) synchronization.push(dispatch('fetchDeliveryOrders'))
        await Promise.all(synchronization)
        return data
      } finally {
        commit('SET_MOTOBOY_OPERATION_PENDING', { motoboyId, pending: false })
      }
    },

    async updateMotoboyAssignment(
      { commit, state, dispatch },
      { orderId, operation, motoboyId, action }
    ) {
      const snapshot = getDashboardSnapshot(state)
      commit('SET_ASSIGNMENT_OPERATION_PENDING', { orderId, pending: true })
      let data
      try {
        const response = await axios.post(
          `/api/sales/delivery/${orderId}/${operation}?action=${action}&motoboyId=${motoboyId}`,
        )
        data = response.data
      } catch (error) {
        commit('RESTORE_DASHBOARD_SNAPSHOT', snapshot)
        commit('SET_ASSIGNMENT_OPERATION_PENDING', { orderId, pending: false })
        throw error
      }

      try {
        const order = data?.order || data?.sale
        let hasAuthoritativeOrder = false
        if (order?.id) {
          commit('UPSERT_ORDER', order)
          hasAuthoritativeOrder = true
        } else if (data?.saleId) {
          const changes = {}
          if (Object.prototype.hasOwnProperty.call(data, 'motoboyId')) {
            changes.motoboyId = data.motoboyId
            // Keep the nested motoboy object in sync so the card reflects the
            // new assignment (and clears it when the motoboy is unassigned).
            changes.motoboy = data.motoboyId
              ? { id: data.motoboyId, name: data.motoboyName ?? null }
              : null
          }
          if (Object.prototype.hasOwnProperty.call(data, 'motoboyName')) {
            changes.motoboyName = data.motoboyName
          }
          if (Object.prototype.hasOwnProperty.call(data, 'assignmentStatus')) {
            changes.motoboyAssignmentStatus = data.assignmentStatus
          }
          if (Object.prototype.hasOwnProperty.call(data, 'autoDeliveryState')) {
            changes.autoDeliveryState = data.autoDeliveryState
          }
          if (data.deliveryStatus !== null && data.deliveryStatus !== undefined) {
            changes.deliveryStatus = data.deliveryStatus
          }
          commit('MERGE_ORDER_DELIVERY_STATE', { orderId: data.saleId, changes })
          hasAuthoritativeOrder = true
        }

        if (Array.isArray(data?.motoboys)) commit('SET_MOTOBOYS', data.motoboys)
        else await dispatch('fetchMotoboys')
        if (!hasAuthoritativeOrder) await dispatch('fetchDeliveryOrders')
        return data
      } finally {
        commit('SET_ASSIGNMENT_OPERATION_PENDING', { orderId, pending: false })
      }
    },

    assignMotoboy({ dispatch }, payload) {
      return dispatch('updateMotoboyAssignment', {
        ...payload,
        operation: 'assign-motoboy',
      })
    },

    replaceMotoboy({ dispatch }, payload) {
      return dispatch('updateMotoboyAssignment', {
        ...payload,
        operation: 'replace-motoboy',
      })
    },

    unassignMotoboy({ dispatch }, { orderId }) {
      return dispatch('updateMotoboyAssignment', {
        orderId,
        operation: 'unassign-motoboy',
      })
    },

    runUnansweredMessageAlarm({ commit, state }) {
      if (state.unansweredMessageAlarmTimer) return
      const timer = setInterval(() => {
        if (
          state.lastTimeHasUnansweredChats &&
          Date.now() - state.lastTimeHasUnansweredChats >= 60000
        ) {
          playSoundNTimes({ sound: alarmSound, times: 5, interval: 2000 })
        }
      }, toMiliseconds({ min: 1 }))
      commit('SET_UNANSWERED_MESSAGE_ALARM_TIMER', timer)
    },

    setFilter({ commit }, filter) {
      commit('SET_FILTER', filter)
    },

    setActiveTab({ commit }, tab) {
      commit('SET_ACTIVE_TAB', tab)
    },

    async fetchDeliverySuppliers({ commit, state }, params = {}) {
      const { name, storeId, active, force } = params || {}
      const key = storeId == null ? 'all' : String(storeId)
      const cached = state.deliverySuppliersByStore[key]
      if (!force && !name && cached) {
        commit('SET_DELIVERY_SUPPLIERS', cached)
        return cached
      }
      const { data } = await axios.get('/api/sales/delivery-suppliers', {
        params: { name, storeId, active },
      })
      const results = data.results || []
      commit('SET_DELIVERY_SUPPLIERS', results)
      if (!name) {
        commit('SET_DELIVERY_SUPPLIERS_FOR_STORE', { storeId, suppliers: results })
      }
      return results
    },

    setOrderDeliveryStatus({ commit }, orderPayload) {
      commit('SET_OR_ADD_ORDER', orderPayload)
    },

    cleanState({ commit, dispatch }) {
      dispatch('disconnectDashboardWebSockets')
      commit('CLEAN_STATE')
    },

    connectToDeliveryWebSocket({ commit, state, dispatch }) {
      const socketState = state.webSocket.delivery
      commit('SET_WEBSOCKET_SHOULD_RECONNECT', { type: 'delivery', shouldReconnect: true })
      if (!socketState.config?.webSocketEndpoint) {
        commit('SET_DELIVERY_READY', true)
        commit('SET_DELIVERY_WEBSOCKET_CONNECTED', false)
        return
      }
      if (
        socketState.ws &&
        [WebSocket.OPEN, WebSocket.CONNECTING].includes(socketState.ws.readyState)
      ) {
        return
      }

      if (socketState.reconnectTimer) {
        clearTimeout(socketState.reconnectTimer)
        commit('SET_WEBSOCKET_RECONNECT_TIMER', { type: 'delivery', timer: null })
      }

      const { webSocketEndpoint } = socketState.config
      console.debug('[delivery websocket] Connecting to', webSocketEndpoint)
      const ws = new WebSocket(`${webSocketEndpoint}?Token=${useJwt.getToken()}`)
      commit('SET_WEBSOCKET_INSTANCE', { type: 'delivery', ws })

      ws.onerror = error => {
        if (state.webSocket.delivery.ws !== ws) return
        commit('SET_DELIVERY_READY', true)
        commit('SET_DELIVERY_WEBSOCKET_CONNECTED', false)
        console.debug('[delivery websocket] Connection error', error)
      }

      ws.onopen = async () => {
        if (state.webSocket.delivery.ws !== ws) return
        console.debug('[delivery websocket] Connection opened')
        commit('SET_WEBSOCKET_RECONNECT_ATTEMPTS', { type: 'delivery', attempts: 0 })
        commit('SET_DELIVERY_SYNCHRONIZING', true)
        commit('SET_DELIVERY_READY', true)
        try {
          await dispatch('joinDeliveryChannel')
          await dispatch('resynchronizeDeliveryDashboard')
          await dispatch('flushPendingDeliveryEvents')
          commit('SET_DELIVERY_WEBSOCKET_CONNECTED', !!state.filter.storeId)
        } catch (error) {
          console.error('[delivery websocket] Resynchronization failed', error)
          commit('SET_DELIVERY_WEBSOCKET_CONNECTED', false)
          if (state.webSocket.delivery.ws === ws) ws.close()
        } finally {
          commit(
            'SET_DELIVERY_SYNCHRONIZING',
            state.webSocket.delivery.ws !== ws || ws.readyState !== WebSocket.OPEN
          )
        }
      }

      ws.onmessage = event => {
        if (state.webSocket.delivery.ws !== ws) return
        const data = JSON.parse(event.data)
        dispatch('handleIncomingDeliveryEvent', data)
      }

      ws.onclose = () => {
        if (state.webSocket.delivery.ws !== ws) return
        console.debug('[delivery websocket] Connection closed')
        commit('SET_WEBSOCKET_INSTANCE', { type: 'delivery', ws: null })
        commit('SET_DELIVERY_WEBSOCKET_CONNECTED', false)
        dispatch('scheduleWebSocketReconnect', 'delivery')
      }
    },

    scheduleWebSocketReconnect({ commit, state, dispatch }, type) {
      const socketState = state.webSocket[type]
      if (!socketState.shouldReconnect || socketState.reconnectTimer) return
      const delay = getReconnectDelay(socketState.reconnectAttempts)
      const timer = setTimeout(() => {
        commit('SET_WEBSOCKET_RECONNECT_TIMER', { type, timer: null })
        commit('SET_WEBSOCKET_RECONNECT_ATTEMPTS', {
          type,
          attempts: socketState.reconnectAttempts + 1,
        })
        dispatch(type === 'delivery' ? 'connectToDeliveryWebSocket' : 'connectToChatWebSocket')
      }, delay)
      commit('SET_WEBSOCKET_RECONNECT_TIMER', { type, timer })
    },

    disconnectWebSocket({ commit, state }, type) {
      const socketState = state.webSocket[type]
      commit('SET_WEBSOCKET_SHOULD_RECONNECT', { type, shouldReconnect: false })
      if (socketState.reconnectTimer) clearTimeout(socketState.reconnectTimer)
      commit('SET_WEBSOCKET_RECONNECT_TIMER', { type, timer: null })
      commit('SET_WEBSOCKET_RECONNECT_ATTEMPTS', { type, attempts: 0 })
      const { ws } = socketState
      if (ws) {
        ws.onopen = null
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(ws.readyState)) ws.close()
      }
      commit('SET_WEBSOCKET_INSTANCE', { type, ws: null })
      commit('SET_WEBSOCKET_CHANNEL', { type, channelId: null })
      if (type === 'delivery') {
        commit('SET_DELIVERY_WEBSOCKET_CONNECTED', false)
        commit('SET_DELIVERY_SYNCHRONIZING', false)
        commit('CLEAR_PENDING_DELIVERY_EVENTS')
      } else {
        commit('SET_CHAT_WEBSOCKET_CONNECTED', false)
      }
    },

    disconnectDeliveryWebSocket({ dispatch }) {
      return dispatch('disconnectWebSocket', 'delivery')
    },

    disconnectChatWebSocket({ dispatch }) {
      return dispatch('disconnectWebSocket', 'chat')
    },

    disconnectDashboardWebSockets({ dispatch, commit, state }) {
      dispatch('disconnectDeliveryWebSocket')
      dispatch('disconnectChatWebSocket')
      if (state.unansweredMessageAlarmTimer) clearInterval(state.unansweredMessageAlarmTimer)
      commit('SET_UNANSWERED_MESSAGE_ALARM_TIMER', null)
    },

    async restartDeliveryWebSocket({ dispatch }) {
      await dispatch('disconnectDeliveryWebSocket')
      await dispatch('connectToDeliveryWebSocket')
    },

    async joinDeliveryChannel({ commit, state }) {
      const { ws } = state.webSocket.delivery
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      if (state.webSocket.delivery.currentChannelId) {
        const message = {
          action: 'leave',
          channelId: state.webSocket.delivery.currentChannelId,
        }
        console.debug('[delivery websocket] Leaving channel ', message.channelId)
        ws.send(JSON.stringify(message))
        commit('SET_WEBSOCKET_CHANNEL', { type: 'delivery', channelId: null })
      }

      if (state.filter.storeId) {
        const message = {
          action: 'join',
          channelId: `store-${state.filter.storeId}-sale-delivery-events`,
        }
        console.debug('[delivery websocket] Joining channel ', message.channelId)
        ws.send(JSON.stringify(message))
        commit('SET_WEBSOCKET_CHANNEL', {
          type: 'delivery',
          channelId: message.channelId,
        })
      }
    },

    processDeliveryEvent({ commit }, data) {
      let payload = data
      if (typeof data?.content === 'string') {
        try {
          payload = JSON.parse(data.content)
        } catch (error) {
          console.error('[delivery websocket] Invalid event content', error)
          return
        }
      } else if (data?.content) {
        payload = data.content
      }
      const eventPayload = payload?.payload || payload
      const orderPayload = eventPayload?.order || eventPayload?.sale
      if (orderPayload?.id) commit('SET_OR_ADD_ORDER', orderPayload)
      else if (eventPayload?.id && (eventPayload.customer || eventPayload.deliveryStatus)) {
        commit('SET_OR_ADD_ORDER', eventPayload)
      }

      if (Array.isArray(eventPayload?.motoboys)) commit('SET_MOTOBOYS', eventPayload.motoboys)
      if (eventPayload?.motoboy) commit('MERGE_MOTOBOY', eventPayload.motoboy)
      else if (
        eventPayload?.motoboyId &&
        (eventPayload?.presenceState || eventPayload?.state)
      ) {
        // Presence-changed events carry `state` (e.g. a delivery being finished
        // frees the motoboy back to Present). Merge it so the card updates live.
        commit('MERGE_MOTOBOY', eventPayload)
      }

      const orderId = eventPayload?.saleId || eventPayload?.orderId
      if (orderId) {
        // Dispatch-changed events carry the dispatch fields flat (dispatchId,
        // executionState, supplierStatus...), not nested under `dispatch`. Rebuild
        // the nested shape so the card's supplier badges update live (e.g. a cancelled
        // supplier order leaving the FindingDriver status).
        //
        // Only copy fields that are actually present on the event. Some dashboard
        // events carry a dispatchId but not the full status set (e.g. WinnerSelected
        // only sends ids). Copying absent fields as `undefined` would overwrite the
        // dispatch's real supplierStatus/isAccepted and revert the badge to "Pending"
        // — which is exactly what happens when a WinnerSelected event lands after the
        // DispatchStatusChanged that set "DriverAccepted".
        let dispatchPayload = eventPayload?.dispatch
        if (!dispatchPayload && eventPayload?.dispatchId) {
          dispatchPayload = { id: eventPayload.dispatchId }
          const copyIfPresent = key => {
            if (eventPayload[key] !== undefined) dispatchPayload[key] = eventPayload[key]
          }
          copyIfPresent('deliverySupplierId')
          copyIfPresent('executionState')
          copyIfPresent('supplierStatus')
          copyIfPresent('supplierOrderId')
          copyIfPresent('isAccepted')
          copyIfPresent('isFailed')
        }
        commit('MERGE_ORDER_DELIVERY_STATE', {
          orderId,
          changes: eventPayload?.orderChanges || eventPayload?.changes,
          dispatch: dispatchPayload,
        })
      }
      commit('SET_LAST_FETCHED')
    },

    handleIncomingDeliveryEvent({ commit, state, dispatch }, data) {
      console.debug('[delivery websocket] receiving data', data)
      if (state.webSocket.delivery.currentChannelId !== data.channelId) {
        console.debug('[delivery websocket] received message from different channel, ignore', data)
        return
      }
      if (!['sendMessage', 'message', 'event'].includes(data.action)) return
      if (state.webSocket.delivery.synchronizing) {
        commit('QUEUE_DELIVERY_EVENT', data)
        return
      }
      dispatch('processDeliveryEvent', data)
    },

    flushPendingDeliveryEvents({ commit, state, dispatch }) {
      const events = [...state.webSocket.delivery.pendingEvents]
      commit('CLEAR_PENDING_DELIVERY_EVENTS')
      events.forEach(event => dispatch('processDeliveryEvent', event))
    },

    connectToChatWebSocket({ commit, state, rootState, dispatch }) {
      const endpoint = rootState.app.chatApiSettings.webSocketEndpoint
      const socketState = state.webSocket.chat
      commit('SET_WEBSOCKET_SHOULD_RECONNECT', { type: 'chat', shouldReconnect: true })
      if (!endpoint) {
        commit('SET_CHAT_READY', true)
        commit('SET_CHAT_WEBSOCKET_CONNECTED', false)
        dispatch('scheduleWebSocketReconnect', 'chat')
        return
      }
      if (
        socketState.ws &&
        [WebSocket.OPEN, WebSocket.CONNECTING].includes(socketState.ws.readyState)
      ) {
        return
      }

      if (socketState.reconnectTimer) {
        clearTimeout(socketState.reconnectTimer)
        commit('SET_WEBSOCKET_RECONNECT_TIMER', { type: 'chat', timer: null })
      }
      console.log('[chat websocket] Connecting to ', endpoint)
      const ws = new WebSocket(`${endpoint}?Token=${useJwt.getToken()}`)
      commit('SET_WEBSOCKET_INSTANCE', { type: 'chat', ws })

      ws.onerror = error => {
        if (state.webSocket.chat.ws !== ws) return
        commit('SET_CHAT_READY', true)
        commit('SET_CHAT_WEBSOCKET_CONNECTED', false)
        console.log('[chat websocket] Connection error', error)
      }

      ws.onopen = () => {
        if (state.webSocket.chat.ws !== ws) return
        console.log('[chat websocket] Connection opened')
        commit('SET_WEBSOCKET_RECONNECT_ATTEMPTS', { type: 'chat', attempts: 0 })
        dispatch('joinChatChannel')
        commit('SET_CHAT_READY', true)
      }

      ws.onmessage = event => {
        if (state.webSocket.chat.ws !== ws) return
        const data = JSON.parse(event.data)
        dispatch('handleIncomingMessage', data)
      }

      ws.onclose = () => {
        if (state.webSocket.chat.ws !== ws) return
        console.log('[chat websocket] Connection closed')
        commit('SET_WEBSOCKET_INSTANCE', { type: 'chat', ws: null })
        commit('SET_CHAT_WEBSOCKET_CONNECTED', false)
        dispatch('scheduleWebSocketReconnect', 'chat')
      }
    },

    handleIncomingMessage({ commit, state, dispatch }, data) {
      console.log('[chat websocket] receiving data', data)
      switch (data.action) {
        case 'message': {
          if (data.userId.indexOf('C-') === 0) {
            notificationMessage.play()
            const message = {
              ...data,
              action: data.channelId === state.activeChat?.channelId ? 'read' : 'delivered',
            }

            state.webSocket.chat.ws.send(JSON.stringify(message))
          }

          dispatch('addChatMessage', {
            ...data,
            userId: data.userId.replace(/^E-/i, ''),
          })
          break
        }
        case 'delivered':
        case 'read':
          commit('SET_MESSAGE_STATUS', data)
          break
        default:
      }
    },

    async joinChatChannel({ commit, state }) {
      const { ws } = state.webSocket.chat
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      if (state.webSocket.chat.currentChannelId) {
        const leaveMessage = {
          action: 'leave',
          channelId: state.webSocket.chat.currentChannelId,
        }
        console.log('[chat websocket] Leaving channel ', leaveMessage.channelId)
        ws.send(JSON.stringify(leaveMessage))
        commit('SET_WEBSOCKET_CHANNEL', { type: 'chat', channelId: null })
      }

      if (state.filter.storeId) {
        const joinMessage = {
          action: 'join',
          channelId: `order-${state.filter.storeId}-*`,
        }
        console.log('[chat websocket] Joining channel ', joinMessage.channelId)
        ws.send(JSON.stringify(joinMessage))
        commit('SET_CHAT_WEBSOCKET_CONNECTED', true)
        commit('SET_WEBSOCKET_CHANNEL', { type: 'chat', channelId: joinMessage.channelId })
      } else {
        commit('SET_CHAT_WEBSOCKET_CONNECTED', false)
      }
    },

    async startChat({ commit, state, rootState }, order) {
      let chat = state.chats.find(chatItem => chatItem.id === order.id)
      if (!chat) {
        // Create a new chat
        chat = {
          ...order,
          channelId: `order-${state.filter.storeId}-${order.id}`,
          messages: [],
          chat: {
            lastMessage: {
              time: Date.now(),
            },
          },
        }
      }

      try {
        chat.messages = (
          await axios.get(
            `${rootState.app.chatApiSettings.webApiEndpoint}/v1/channels/${chat.channelId}/messages`
          )
        ).data.map(message => ({
          ...message,
          userId: message.userId.replace(/^E-/i, ''),
        }))
      } catch (error) {
        chat.messages = []
        console.log('Error getting chat messages: ', error)
      }

      commit('ADD_CHAT', chat)
      commit('SET_ACTIVE_CHAT', chat)
    },

    quitChat({ commit }) {
      commit('CLEAR_ACTIVE_CHAT')
    },

    readUnreadMessages({ commit, state }) {
      const unreadMessages = state.activeChat.messages.filter(
        message => message.userId.indexOf('C-') === 0
      )
      unreadMessages.forEach(message => {
        const readMessage = message
        readMessage.action = 'read'
        state.webSocket.chat.ws.send(JSON.stringify(message))
      })
      commit('SET_READ_MESSAGES', unreadMessages)
    },

    async loadActiveChats({ commit, state, rootState }) {
      if (state.webSocket.chat.currentChannelId) {
        const activeChats = await axios.get(
          `${
            rootState.app.chatApiSettings.webApiEndpoint
          }/v1/channels/${state.webSocket.chat.currentChannelId.replace('*', '')}`
        )
        commit(
          'SET_ACTIVE_CHATS',
          activeChats.data?.map(chat => ({
            ...chat,
          }))
        )
      }
    },

    addChatMessage({ commit, state, dispatch }, message) {
      let currentChat = state.chats.find(chat => chat.channelId === message.channelId)
      if (!currentChat) {
        const [orderId] = message.channelId.split('-').slice(-1)
        // eslint-disable-next-line eqeqeq
        const order = state.allOrders.find(orderItem => orderItem.id == orderId)
        dispatch('startChat', order)
        currentChat = state.chats.find(chat => chat.channelId === message.channelId)
      }
      commit('ADD_CHAT_MESSAGE', message)
    },

    sendChatMessage({ commit, state, dispatch }, message) {
      let currentChat = state.chats.find(chat => chat.channelId === message.channelId)
      if (!currentChat) {
        const [orderId] = message.channelId.split('-').slice(-1)
        const order = state.allOrders.find(orderItem => orderItem.id === orderId)
        dispatch('startChat', order)
        currentChat = state.chats.find(chat => chat.channelId === message.channelId)
      }
      commit('ADD_CHAT_MESSAGE', message)
      state.webSocket.chat.ws.send(JSON.stringify(message))
    },

    editChatMessage({ state }, message) {
      const existingMessage = state.activeChat.messages.find(
        messageItem => messageItem.messageId === message.messageId
      )
      existingMessage.content = message.content
      state.webSocket.chat.ws.send(JSON.stringify(message))
    },

    deleteChatMessage({ state }, message) {
      const messageIndex = state.activeChat.messages.findIndex(
        messageItem => messageItem.messageId === message.messageId
      )
      state.activeChat.messages.splice(messageIndex, 1)
      state.webSocket.chat.ws.send(JSON.stringify(message))
    },

    async loadOrderItems({ commit, state }, orderId) {
      if (!state.orderItems[orderId]) {
        const items = (await axios.get(`/api/sales/${orderId}/items`)).data
        commit('SET_ORDER_ITEMS', { orderId, items })
      }
    },
    setLastTimeHasUnansweredChats({ state }, lastTime) {
      state.lastTimeHasUnansweredChats = lastTime
    },

    async createDeliveryOrder({ commit, state }, { orderId, estimateId, deliverySupplier }) {
      const { data } = await axios.post(
        `/api/sales/pay-box-sale/delivery/create-order/${orderId}`,
        {
          estimateId,
          deliverySupplier,
        }
      )

      const supplierFromCache = (state.deliverySuppliers || []).find(
        s => s?.supplier === deliverySupplier
      )

      commit('SET_DELIVERY_ORDER', {
        orderId,
        deliveryOrderId: data.orderId,
        deliverySupplier: supplierFromCache
          ? {
              id: supplierFromCache.id,
              name: supplierFromCache.name,
              supplier: supplierFromCache.supplier,
            }
          : null,
      })
    },

    async cancelDeliveryOrder({ commit }, orderId) {
      await axios.post(`/api/sales/pay-box-sale/delivery/cancel-order/${orderId}`)

      commit('SET_DELIVERY_ORDER', { orderId, deliveryOrderId: null })
    },
  },
}
